import { Link } from "react-router-dom";
import { Phone, ArrowRight } from "lucide-react";
import HeroAnimated from "@/components/HeroAnimated";
import ServiceOverviewBand from "@/components/ServiceOverviewBand";
import GoogleReviewsSlider from "@/components/GoogleReviewsSlider";
import WaveDivider from "@/components/WaveDivider";
import FlowingWaves from "@/components/FlowingWaves";
import Reveal from "@/components/SectionReveal";
import { useSeo, localBusinessJsonLd } from "@/lib/seo";
import { SITE, SERVICES, CITIES } from "@/data/site";

export default function HomePage() {
  useSeo({
    title: "Pool Cleaning, Service & 24/7 Emergency in Tampa Bay, FL",
    description:
      "Tampa Bay Pool Pros — local, licensed pool cleaning, weekly service, green pool cleanup, filter & pump cleaning, and 24/7 pool emergency repair across Clearwater, Tarpon Springs, Trinity, Dunedin, Palm Harbor, Oldsmar, Westchase, Town & Country, Odessa, and Carrollwood. 25+ years experience. Free quotes.",
    path: "/",
    image: SITE.logo,
    jsonLd: localBusinessJsonLd(),
  });

  const featured = SERVICES.slice(0, 6);

  return (
    <main className="relative">
      <FlowingWaves />
      <div className="relative" style={{ zIndex: 1 }}>
        <HeroAnimated />
        <ServiceOverviewBand />
        <GoogleReviewsSlider />

      {/* Services preview */}
      <section className="py-16 sm:py-24 bg-white/80 backdrop-blur-[2px]">
        <div className="max-w-7xl mx-auto px-6 sm:px-10">
          <Reveal className="max-w-3xl">
            <p className="text-xs tracking-[0.22em] uppercase font-bold text-sky-700">
              Our Pool Services
            </p>
            <h2 className="mt-3 font-heading text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-slate-900">
              Pool cleaning &amp; service done right, every time.
            </h2>
            <p className="mt-4 text-slate-600">
              From routine weekly pool cleaning to full green-pool recovery and 24/7
              emergency repair, our pros have you covered across Tampa Bay.
            </p>
          </Reveal>

          <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {featured.map((s, i) => (
              <Reveal
                key={s.slug}
                delay={i * 0.04}
                direction={i % 2 === 0 ? "up" : "left"}
                testId={`home-service-${s.slug}`}
                className={`group rounded-3xl p-6 border transition-all hover:-translate-y-1 ${
                  s.featured
                    ? "bg-gradient-to-br from-sky-600 to-cyan-500 text-white border-transparent shadow-xl shadow-sky-500/30"
                    : "bg-white text-slate-900 border-slate-100 shadow-[0_8px_30px_rgba(0,0,0,0.04)]"
                }`}
              >
                <h3 className="font-heading font-bold text-xl">{s.title}</h3>
                <p
                  className={`mt-2 text-sm leading-relaxed ${
                    s.featured ? "text-white/90" : "text-slate-600"
                  }`}
                >
                  {s.blurb}
                </p>
                <Link
                  to={`/services/${s.slug}`}
                  className={`mt-4 inline-flex items-center gap-1 text-sm font-bold ${
                    s.featured ? "text-amber-300" : "text-sky-700"
                  }`}
                  data-testid={`home-service-link-${s.slug}`}
                >
                  Learn more <ArrowRight className="w-3.5 h-3.5" />
                </Link>
              </Reveal>
            ))}
          </div>

          <div className="mt-10 text-center">
            <Link
              to="/services"
              data-testid="home-all-services"
              className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-slate-900 text-white font-bold hover:bg-sky-700 transition-colors"
            >
              See all pool services
              <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* Service areas preview */}
      <section className="relative py-16 sm:py-24 bg-gradient-to-b from-sky-50/60 to-white/70 backdrop-blur-[2px]">
        <WaveDivider placement="top" fromColor="#ffffff" className="absolute -top-[1px] inset-x-0" />
        <div className="max-w-7xl mx-auto px-6 sm:px-10">
          <Reveal className="max-w-3xl">
            <p className="text-xs tracking-[0.22em] uppercase font-bold text-sky-700">
              Service Areas
            </p>
            <h2 className="mt-3 font-heading text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-slate-900">
              Proudly serving Tampa Bay.
            </h2>
          </Reveal>

          <div className="mt-10 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
            {CITIES.map((c, i) => (
              <Reveal
                key={c.slug}
                delay={i * 0.02}
                testId={`home-area-${c.slug}`}
              >
                <Link
                  to={`/service-areas/${c.slug}`}
                  className="block rounded-2xl bg-white border border-slate-100 shadow-[0_4px_20px_rgba(0,0,0,0.04)] hover:-translate-y-0.5 hover:border-sky-300 transition-all p-4 text-center"
                >
                  <p className="font-heading font-bold text-slate-900 text-sm sm:text-base">
                    {c.name}
                  </p>
                  <p className="text-[10px] uppercase tracking-[0.18em] text-sky-700 mt-1">
                    Pool service
                  </p>
                </Link>
              </Reveal>
            ))}
          </div>

          <div className="mt-10 text-center">
            <Link
              to="/service-areas"
              data-testid="home-all-areas"
              className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-white border border-slate-200 text-slate-900 font-bold hover:border-sky-500 hover:text-sky-700 transition-colors"
            >
              All service areas
              <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* CTA band */}
      <section className="relative py-16 sm:py-20 bg-slate-950 text-white">
        <WaveDivider placement="top" fromColor="#ffffff" className="absolute -top-[1px] inset-x-0" />
        <div className="max-w-5xl mx-auto px-6 sm:px-10 text-center">
          <Reveal>
            <p className="text-xs tracking-[0.22em] uppercase font-bold text-amber-400">
              25+ Years Experience · Est. {SITE.established}
            </p>
            <h2 className="mt-3 font-heading text-3xl sm:text-5xl font-extrabold tracking-tight">
              Ready for a clean, worry-free pool?
            </h2>
            <p className="mt-4 text-slate-300 max-w-2xl mx-auto">
              Talk to a Tampa Bay Pool Pro today — free quotes, transparent pricing, 24/7 emergency line.
            </p>
            <div className="mt-7 flex flex-col sm:flex-row gap-3 justify-center">
              <Link
                to="/contact"
                className="inline-flex items-center justify-center gap-2 px-7 py-4 rounded-full bg-amber-300 text-slate-900 font-bold hover:bg-amber-200"
                data-testid="home-cta-quote"
              >
                Get a free quote
              </Link>
              <a
                href={`tel:${SITE.phoneTel}`}
                className="inline-flex items-center justify-center gap-2 px-7 py-4 rounded-full border border-white/30 text-white font-bold hover:bg-white/10"
                data-testid="home-cta-call"
              >
                <Phone className="w-4 h-4" /> Call {SITE.phoneDisplay}
              </a>
            </div>
          </Reveal>
        </div>
      </section>
      </div>
    </main>
  );
}
